import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Cattura le eccezioni lanciate durante il render dei figli ed evita che
 * l'intera pagina diventi bianca. Mostra un messaggio leggibile, l'errore
 * reale (utile per il debug) e un pulsante per ricaricare.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Lasciamo traccia in console: l'errore resta visibile in produzione
    // anche al cliente che apre la console del browser.
    console.error('ErrorBoundary ha catturato un errore:', error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6 text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Qualcosa è andato storto
          </h1>
          <p className="text-gray-600 mb-6 max-w-md">
            Si è verificato un errore imprevisto. Prova a ricaricare la pagina.
            Se il problema persiste, contatta l'assistenza.
          </p>
          <button
            onClick={this.handleReload}
            className="px-5 py-2.5 rounded-lg bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors"
          >
            Ricarica la pagina
          </button>
          {this.state.error && (
            <details className="mt-6 max-w-lg text-left">
              <summary className="cursor-pointer text-sm text-gray-500">
                Dettagli tecnici
              </summary>
              <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-red-600 bg-red-50 p-3 rounded">
                {this.state.error.message}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
